import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  HelpAnchor,
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePagePanel,
  SurfacePageSectionHeader,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TAB_INACTIVE } from '@/shared/ui-class';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { REPORT_CASE_PINS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import {
  type DailyAccountingReportView,
  type ReportsTab,
  type TemplateRegistryRow,
  advisoriesByState,
  dailyAccountingReportView,
  dailyManagementReportView,
  DAILY_REPORT_SECTION_NAV,
  reportsCopy,
  reportsTabFromParam,
  shiftReportTables,
  templateHeaderLine,
  templateRegistryRows,
  visibleReportsTabs,
  workflowMeta,
  workflowTabChip,
  workflowTabChipTitle,
  weeklyMetricRows,
  weeklyPeriodHeader,
  weeklySummarySubtitle,
  weeklySummaryTable,
} from './Scene.transform';

const HELP_DOC: Record<ReportsTab, string> = {
  'daily-management': 'reports/daily-management',
  'daily-accounting': 'reports/daily-accounting',
  weekly: 'reports/weekly',
  'shift-report': 'reports/shift-report',
};

export function ReportsScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const plantName = project?.name ?? 'this plant';
  const copy = reportsCopy(plantName);
  const { state } = useScenario();
  const advisory = useAdvisory();
  const { can } = useRole();
  const showMoney = can('money:view');
  const reportTabs = visibleReportsTabs(showMoney);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<ReportsTab>(() => reportsTabFromParam(searchParams.get('tab'), reportTabs));
  const [emailPreview, setEmailPreview] = useState(false);
  const registryRows = templateRegistryRows(showMoney);
  const dailyView = project
    ? dailyManagementReportView(project.slug, state, advisory, state.cursor, showMoney)
    : null;
  const accountingView = dailyAccountingReportView();

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const tabWorkflowChip = workflowTabChip(tab);
  const tabWorkflowChipTitle = workflowTabChipTitle(tab);
  const tabWorkflowMeta = workflowMeta(tab);
  const templateHeader = templateHeaderLine(tab);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ReportsScene">
        <SurfacePageBody className="space-y-4" width="full">
          <SurfacePageHeader
            title={copy.title}
            description={copy.description}
            titleSize="large"
            tabs={
              <SurfacePageTabs
                aria-label="Reports"
                items={reportTabs}
                value={tab}
                onChange={(next) => {
                  setTab(next);
                  if (next !== 'daily-management') setEmailPreview(false);
                }}
              />
            }
            actions={
              <span className="inline-flex items-center gap-2">
                <Pill>v1.1</Pill>
                <HelpAnchor doc={HELP_DOC[tab]} label="Reports" />
              </span>
            }
          />

          {tab === 'daily-management' && dailyView ? (
            <Commentable name="DailyManagementReport">
              <DailyManagementReportTab
                projectSlug={project.slug}
                state={state}
                advisory={advisory}
                emailPreview={emailPreview}
                onEmailPreviewChange={setEmailPreview}
                onOpenShiftReport={() => setTab('shift-report')}
                workflowChip={tabWorkflowChip}
                workflowChipTitle={tabWorkflowChipTitle}
                workflowMeta={tabWorkflowMeta}
                evaluatedAgainstChip={dailyView.evaluatedAgainstChip}
              />
            </Commentable>
          ) : null}

          {/* C41 #438: one registry disclosure, shared by every tab and closed
              by default, so the expanded registry no longer leads any tab (C32
              #396). On the accounting / weekly / shift tabs it sits between the
              tabs and the report header; the daily tab keeps its C32 slot at
              the end of its body. */}
          <Commentable name="TemplateRegistry">
            <TemplateRegistryDisclosure
              rows={registryRows}
              activeTab={tab}
              onSelectTab={setTab}
            />
          </Commentable>

          {tab !== 'daily-management' && (tabWorkflowChip || tabWorkflowMeta || templateHeader) ? (
            <ReportWorkflowProvenance
              chip={tabWorkflowChip}
              chipTitle={tabWorkflowChipTitle}
              meta={tabWorkflowMeta}
              header={templateHeader}
            />
          ) : null}

          {tab === 'daily-accounting' ? (
            <Commentable name="DailyAccountingReport">
              <DailyAccountingReportTab view={accountingView} />
            </Commentable>
          ) : null}

          {tab === 'weekly' ? (
            <Commentable name="Weekly">
              <WeeklyReportTab plantName={project.name} projectSlug={project.slug} cursor={state.cursor} state={state} />
            </Commentable>
          ) : null}

          {tab === 'shift-report' ? (
            <Commentable name="ShiftReport">
              <ShiftReportTab />
            </Commentable>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

const SENT_EDITION_CUTOFF = '2026-06-08T08:00' as SeriesTime;

/** The one template-registry disclosure (ui-lift.C32 #396, shared across every
 *  Reports tab by ui-lift.C41 #438). Native `<details>`, closed by default —
 *  no `open` attribute and nothing that opens it. */
function TemplateRegistryDisclosure({
  rows,
  activeTab,
  onSelectTab,
}: {
  rows: readonly TemplateRegistryRow[];
  activeTab: ReportsTab;
  onSelectTab: (tab: ReportsTab) => void;
}) {
  return (
    <details className="rounded-lg border border-[var(--glass-border)] p-3">
      <summary className="cursor-pointer text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Templates ({rows.length})
      </summary>
      <div className="mt-3 overflow-x-auto">
        <Table clickableRows>
          <THead>
            <TR>
              <TH>Template</TH>
              <TH>Audience</TH>
              <TH>Cadence</TH>
              <TH>Channel</TH>
              <TH>Last instance</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR
                key={row.templateId}
                onRowClick={() => onSelectTab(row.tabId)}
                className={activeTab === row.tabId ? 'bg-accent-soft/40' : undefined}
              >
                <TD>
                  <div className="space-y-1">
                    <p
                      className="font-medium text-zinc-900 dark:text-zinc-100"
                      title={row.workflowChipTitle || undefined}
                    >
                      {row.title}
                    </p>
                    {row.evaluatedAgainstChip ? (
                      <div>
                        <ProvenanceChip text={row.evaluatedAgainstChip} compact />
                      </div>
                    ) : null}
                  </div>
                </TD>
                <TD>{row.audience}</TD>
                <TD>{row.cadence}</TD>
                <TD>{row.channel}</TD>
                <TD className="tabular text-sm">{row.lastInstance}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </details>
  );
}

function DailyManagementReportTab({
  projectSlug,
  state,
  advisory,
  emailPreview,
  onEmailPreviewChange,
  onOpenShiftReport,
  workflowChip,
  workflowChipTitle,
  workflowMeta,
  evaluatedAgainstChip,
}: {
  projectSlug: string;
  state: SessionState;
  advisory: AdvisorySessionState;
  emailPreview: boolean;
  onEmailPreviewChange: (next: boolean) => void;
  onOpenShiftReport: () => void;
  workflowChip: string;
  workflowChipTitle?: string;
  workflowMeta: string;
  evaluatedAgainstChip: string;
}) {
  const { can } = useRole();
  const showMoney = can('money:view');
  const [editionId, setEditionId] = useState<'sent' | 'current'>('current');
  const [decisionHistoryOpen, setDecisionHistoryOpen] = useState(false);
  const editionCutoff = editionId === 'sent' ? SENT_EDITION_CUTOFF : state.cursor;
  const view = useMemo(
    () => dailyManagementReportView(projectSlug, state, advisory, editionCutoff, showMoney),
    [projectSlug, state, advisory, editionCutoff, showMoney],
  );
  const sectionNav = useMemo(
    () =>
      DAILY_REPORT_SECTION_NAV.filter(
        (item) =>
          (item.id !== 'daily-report-financial' || view.financialRows.length > 0) &&
          (item.id !== 'daily-report-tracked-issue' || view.trackedIssues.length > 0),
      ),
    [view.financialRows.length, view.trackedIssues.length],
  );
  const [activeSection, setActiveSection] = useState(
    () => sectionNav[0]?.id ?? 'daily-report-executive-summary',
  );
  const paginate = view.paginateBySection && !emailPreview;
  useEffect(() => {
    if (!sectionNav.some((item) => item.id === activeSection)) {
      setActiveSection(sectionNav[0]?.id ?? 'daily-report-executive-summary');
    }
  }, [sectionNav, activeSection]);
  const showSection = (id: string) => !paginate || activeSection === id;
  const activeEdition = view.editions.find((row) => row.id === editionId) ?? view.editions[0];

  const body = (
    <div className="space-y-8">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          {workflowChip ? (
            <span title={workflowChipTitle ?? workflowChip}>
              <ProvenanceChip text={workflowChip} />
            </span>
          ) : null}
          {evaluatedAgainstChip ? <ProvenanceChip text={evaluatedAgainstChip} compact /> : null}
        </div>
        {workflowMeta ? <p className="text-sm text-muted">{workflowMeta}</p> : null}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        {view.comparisonBasisLabel ? (
          <p className="text-sm text-muted">comparison basis: {view.comparisonBasisLabel}</p>
        ) : (
          <span />
        )}
        <span className="inline-flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            className={emailPreview ? 'ring-1 ring-accent-500' : undefined}
            onClick={() => onEmailPreviewChange(!emailPreview)}
          >
            {emailPreview ? 'Exit email preview' : 'Preview as email'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={!activeEdition?.editBeforeSend}
            title={
              activeEdition?.editBeforeSend
                ? 'Edit the unsent next edition before send'
                : activeEdition?.editDisabledReason
            }
          >
            Edit before send <Pill className="ml-2">v1.1</Pill>
          </Button>
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {activeEdition?.label}
        </p>
        <div className="inline-flex gap-1">
          {view.editions.map((edition) => (
            <button
              key={edition.id}
              type="button"
              className={cn(
                SURFACE_PAGE_TAB_INACTIVE,
                editionId === edition.id && 'ring-1 ring-accent-500',
              )}
              onClick={() => setEditionId(edition.id)}
            >
              {edition.id === 'sent' ? 'Sent edition' : 'Current'}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted">{view.decisionWindowLabel}</p>

      {!emailPreview ? (
        <nav
          aria-label="Report sections"
          className={cn(
            'sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-[var(--hairline)] bg-[var(--surface-2)] py-2',
          )}
        >
          {sectionNav.map((item) => (
            <button
              key={item.id}
              type="button"
              className={cn(
                SURFACE_PAGE_TAB_INACTIVE,
                paginate && activeSection === item.id && 'ring-1 ring-accent-500',
              )}
              onClick={() => {
                if (paginate) {
                  setActiveSection(item.id);
                  return;
                }
                document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
      ) : null}

      {showSection('daily-report-tracked-issue') && view.trackedIssues.length > 0 ? (
        <ReportSection id="daily-report-tracked-issue" title="Tracked issue">
          {view.trackedIssues.map((issue) => {
            const pin = REPORT_CASE_PINS.find((row) => row.caseId === issue.caseId);
            return (
            <div key={issue.caseId} className="space-y-1 text-sm">
              {pin?.provenance.startsWith('[syn]') ? (
                <ProvenanceChip text={pin.provenance} compact />
              ) : null}
              <p className="font-medium text-zinc-900 dark:text-zinc-100">{issue.title}</p>
              <p className="text-muted">{issue.recoveryLine}</p>
              <p className="text-muted">{issue.latestDecision}</p>
              <Link
                to={issue.drawerHref}
                className="font-medium text-accent-700 dark:text-accent-400"
              >
                Open case →
              </Link>
            </div>
            );
          })}
        </ReportSection>
      ) : null}

      {showSection('daily-report-executive-summary') ? (
      <ReportSection id="daily-report-executive-summary" title="Executive Summary">
        <ul className="list-disc space-y-2 pl-5 text-sm">
          {view.executiveBullets.map((bullet) => (
            <li key={bullet.text}>
              {bullet.sectionId ? (
                <button
                  type="button"
                  className="text-left text-accent-700 dark:text-accent-400"
                  onClick={() => {
                    if (paginate) {
                      setActiveSection(bullet.sectionId!);
                    }
                    window.requestAnimationFrame(() => {
                      document
                        .getElementById(bullet.sectionId!)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                  }}
                >
                  {bullet.text}
                </button>
              ) : (
                bullet.text
              )}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm">{view.aiSummary}</p>
      </ReportSection>
      ) : null}

      {showSection('daily-report-production') ? (
      <ReportSection id="daily-report-production" title="Production">
        <LabelValueList rows={view.productionRows} />
      </ReportSection>
      ) : null}

      {showSection('daily-report-recovery') ? (
      <ReportSection id="daily-report-recovery" title="Recovery & Grade Performance">
        <div className="space-y-4">
          {view.recoveryRows.map((row) => (
            <div key={row.metal} className="space-y-1 text-sm">
              <p className="font-medium text-zinc-900 dark:text-zinc-100">
                {row.metal}: {row.actual} vs {row.basisLabel} {row.target}
                {row.designSecondary ? (
                  <span className="ml-2 font-normal text-muted">design {row.designSecondary}</span>
                ) : null}
              </p>
              {row.aiInsight ? <p className="text-muted">{row.aiInsight}</p> : null}
            </div>
          ))}
        </div>
      </ReportSection>
      ) : null}

      {showSection('daily-report-impurities') ? (
      <ReportSection id="daily-report-impurities" title="Impurities">
        <LabelValueList rows={view.impurityRows} />
      </ReportSection>
      ) : null}

      {showSection('daily-report-financial') && view.financialRows.length > 0 ? (
      <ReportSection id="daily-report-financial" title="Financial Snapshot">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Pill>{view.financialBasisChip}</Pill>
          <ProvenanceChip text="[syn]" title={view.financialBasisProvenance} compact />
        </div>
        <LabelValueList rows={view.financialRows} />
      </ReportSection>
      ) : null}

      {showSection('daily-report-stability') ? (
      <ReportSection id="daily-report-stability" title="Stability & Reliability Indicators">
        <LabelValueList rows={[{ label: 'Availability', value: view.availability }]} />
        <div className="mt-4 space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            AI-detected emerging risks
          </p>
          {view.emergingRisks.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {view.emergingRisks.map((risk) => (
                <li key={risk}>{risk}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">n/d</p>
          )}
        </div>
      </ReportSection>
      ) : null}

      {showSection('daily-report-environmental') ? (
      <ReportSection id="daily-report-environmental" title="Environmental & Compliance">
        <div className="space-y-3">
          {view.environmentalRows.map((row) => (
            <div key={row.label} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div>
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{row.label}</p>
                <p className="tabular text-muted">
                  {row.value} · limit {row.limit}
                </p>
                {row.provenance ? (
                  <ProvenanceChip text="[syn]" title={row.provenance} compact />
                ) : null}
              </div>
              <Pill tone={row.stateTone}>{row.stateLabel}</Pill>
            </div>
          ))}
        </div>
      </ReportSection>
      ) : null}

      {showSection('daily-report-forecasting') ? (
      <ReportSection id="daily-report-forecasting" title="Forecasting Snapshot">
        <LabelValueList rows={view.forecastingLines} />
        <p className="mt-3 text-sm">
          <Link
            to={view.forecastingProvenance.href}
            className="font-medium text-accent-700 dark:text-accent-400"
          >
            {view.forecastingProvenance.label} →
          </Link>
        </p>
      </ReportSection>
      ) : null}

      {showSection('daily-report-actions') ? (
      <ReportSection id="daily-report-actions" title="Recommended Actions (AI-Generated)">
        <ul className="space-y-2 text-sm">
          {view.recommendedActions.map((action) => (
            <li key={action.actionId} className="flex flex-wrap gap-x-2 gap-y-1">
              <span className="font-medium text-zinc-900 dark:text-zinc-100">{action.text}</span>
              <span className="text-muted">· {action.urgency}</span>
              <span className="text-muted">· {action.disposition}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4">
          <button
            type="button"
            className="text-sm font-medium text-accent-700 dark:text-accent-400"
            onClick={() => setDecisionHistoryOpen((open) => !open)}
          >
            Decision record history
          </button>
        </div>

        {decisionHistoryOpen ? (
        <div className="mt-4 space-y-5 border-t border-divider pt-5">
          {view.shiftActionGroups.map((group) => (
            <div key={group.roleId} className="space-y-2">
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {group.roleLabel}
              </p>
              {group.rows.length === 0 ? (
                <p className="text-sm text-muted">No recommendations raised this shift.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Recommendation</TH>
                      <TH>Decision</TH>
                      <TH>Raised → decided</TH>
                      <TH>Decided → implemented</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {group.rows.map((row) => (
                      <TR key={row.actionId}>
                        <TD className="max-w-sm">
                          <span className="text-zinc-900 dark:text-zinc-100">{row.text}</span>
                          <span className="text-muted"> · {row.urgency}</span>
                          {row.secondary ? (
                            <span className="ml-1 text-xs text-muted">(secondary)</span>
                          ) : null}
                        </TD>
                        <TD>
                          <span className="text-zinc-900 dark:text-zinc-100">{row.statusLabel}</span>
                          {row.deciderLabel ? (
                            <span className="text-muted"> · {row.deciderLabel}</span>
                          ) : null}
                          {row.reason ? (
                            <span className="block text-xs text-muted">Reason: {row.reason}</span>
                          ) : null}
                          {row.fmeaReviewId && row.fmeaStageLabel ? (
                            <Link
                              to={`/projects/${projectSlug}/advisory?tab=approvals&review=${row.fmeaReviewId}`}
                              className="mt-1 block text-xs text-accent-700 dark:text-accent-400"
                            >
                              {row.fmeaStageLabel} →
                            </Link>
                          ) : null}
                        </TD>
                        <TD className="tabular">{row.raisedToDecidedElapsed}</TD>
                        <TD className="tabular">{row.decidedToImplementedElapsed}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </div>
          ))}
        </div>
        ) : null}
      </ReportSection>
      ) : null}

      {!paginate || activeSection === 'daily-report-actions' ? (
      <section className="space-y-2 border-t border-divider pt-6">
        <SurfacePageSectionHeader title="Guided links for deeper layers" />
        <ul className="space-y-2 text-sm">
          {view.guidedLinks.map((link) => (
            <li key={link.label}>
              {link.tab ? (
                <button
                  type="button"
                  className="font-medium text-accent-700 dark:text-accent-400"
                  onClick={onOpenShiftReport}
                >
                  {link.label} →
                </button>
              ) : (
                <Link
                  to={link.href}
                  className="font-medium text-accent-700 dark:text-accent-400"
                >
                  {link.label} →
                </Link>
              )}
            </li>
          ))}
        </ul>
      </section>
      ) : null}
    </div>
  );

  if (emailPreview) {
    return (
      <div className="mx-auto max-w-[640px] rounded-lg border border-divider bg-zinc-50 px-6 py-8 text-zinc-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
          Email preview · narrow column
        </p>
        {view.workflowProvenanceLine ? (
          <p className="mb-6 text-xs text-muted">{view.workflowProvenanceLine}</p>
        ) : (
          <div className="mb-6" />
        )}
        {body}
      </div>
    );
  }

  return <div className="card-matte-2 px-6 py-5">{body}</div>;
}

function ReportWorkflowProvenance({
  chip,
  chipTitle,
  meta,
  header,
  evaluatedAgainst,
}: {
  chip: string;
  chipTitle?: string;
  meta: string;
  header?: string;
  evaluatedAgainst?: string;
}) {
  return (
    <div className="space-y-1">
      {chip ? (
        <span title={chipTitle ?? chip}>
          <ProvenanceChip text={chip} />
        </span>
      ) : null}
      {evaluatedAgainst ? <ProvenanceChip text={evaluatedAgainst} /> : null}
      {meta ? <p className="text-sm text-muted">{meta}</p> : null}
      {header ? <p className="text-sm text-muted">{header}</p> : null}
    </div>
  );
}

function ReportSection({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-14 space-y-3">
      <SurfacePageSectionHeader title={title} />
      {children}
    </section>
  );
}

function LabelValueList({
  rows,
}: {
  rows: readonly { label: string; value: string; muted?: boolean }[];
}) {
  return (
    <dl className="space-y-2 text-sm">
      {rows.map((row) => (
        <div key={row.label} className="grid gap-1 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-4">
          <dt className="text-muted">{row.label}</dt>
          <dd
            className={
              row.muted
                ? 'text-muted'
                : 'font-medium text-zinc-900 dark:text-zinc-100'
            }
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function WeeklyReportTab({
  plantName,
  projectSlug,
  cursor,
  state,
}: {
  plantName: string;
  projectSlug: string;
  cursor: string;
  state: SessionState;
}) {
  const table = weeklySummaryTable();
  const advisories = advisoriesByState();
  const fmeaPending = advisories.find((row) => row.state === 'fmea-review')?.count ?? 0;

  if (!table) return null;

  return (
    <div className="space-y-4">
      <SurfacePagePanel title="Reporting period">
        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {weeklyPeriodHeader(plantName, cursor)}
        </p>
      </SurfacePagePanel>

      <SurfacePagePanel
        title="Metrics vs design — week to date"
        subtitle={weeklySummarySubtitle(table)}
      >
        <RoleTableView table={weeklyMetricRows(table, state)} />
      </SurfacePagePanel>

      <SurfacePagePanel title="Advisories this week">
        <Table>
          <THead>
            <TR>
              <TH>State</TH>
              <TH>Count</TH>
            </TR>
          </THead>
          <TBody>
            {advisories.map((row) => (
              <TR key={row.state}>
                <TD className="font-medium text-zinc-900 dark:text-zinc-100">{row.label}</TD>
                <TD className="tabular">{row.count}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {fmeaPending > 0 ? (
          <p className="text-sm text-muted">
            {fmeaPending} awaiting FMEA sign-off{' '}
            <Link
              to={`${projectSidebarHref(projectSlug, 'advisory')}?tab=approvals`}
              className="font-medium text-accent-700 dark:text-accent-400"
            >
              → Approvals
            </Link>
          </p>
        ) : null}
      </SurfacePagePanel>
    </div>
  );
}

function ShiftReportTab() {
  const tables = shiftReportTables();

  return (
    <div className="space-y-8">
      {tables.map((table) => (
        <SurfacePagePanel key={table.caption} title="Shift report">
          <RoleTableView table={table} />
        </SurfacePagePanel>
      ))}
    </div>
  );
}

function RoleTableView({ table }: { table: RoleTable }) {
  return (
    <Table>
      <THead>
        <TR>
          {table.columns.map((column) => (
            <TH key={column}>{column}</TH>
          ))}
        </TR>
      </THead>
      <TBody>
        {table.rows.map((row) => (
          <TR key={row.id}>
            {row.cells.map((cell, index) => (
              <TD
                key={`${row.id}-${index}`}
                className={
                  index === 0
                    ? 'font-medium text-zinc-900 dark:text-zinc-100'
                    : 'tabular text-sm'
                }
              >
                {cell}
              </TD>
            ))}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function DailyAccountingReportTab({ view }: { view: DailyAccountingReportView }) {
  return (
    <SurfacePagePanel title="Daily accounting report">
      <div className="space-y-6">
        <ProvenanceChip text={view.designBasisScope} title={view.provenance} />
      <ReportSection id="daily-accounting-production" title="Production summary">
        <LabelValueList rows={view.productionSummary} />
      </ReportSection>
      <ReportSection id="daily-accounting-balance" title="Balance by node">
        <LabelValueList rows={view.balanceByNode} />
      </ReportSection>
      <ReportSection id="daily-accounting-adjustments" title="Reconciliation adjustments">
        <LabelValueList rows={view.reconciliationAdjustments} />
      </ReportSection>
      <ReportSection id="daily-accounting-inventory" title="Inventory">
        <LabelValueList rows={view.inventory} />
      </ReportSection>
      <ReportSection id="daily-accounting-signoff" title="Sign-off">
        <Pill tone="default">{view.signOff}</Pill>
      </ReportSection>
      </div>
    </SurfacePagePanel>
  );
}
